import { z } from 'zod';
import { SUPPORTED_LOCALES } from '../constants/app.js';
import { compareDecimal } from '../utils/decimal.js';
import { countryCodeSchema, idSchema, isoDateSchema, isoDateTimeSchema, moneySchema, } from './common.js';
export const PRODUCT_USAGE_ROLES = [
    'TRADING_GOOD',
    'RAW_MATERIAL',
    'SEMI_FINISHED',
    'FINISHED_GOOD',
    'PACKAGING_MATERIAL',
    'OPERATING_SUPPLY',
    'BY_PRODUCT',
];
export const PRODUCT_MASTER_PROFILE_STATUSES = [
    'DRAFT',
    'IN_REVIEW',
    'APPROVED',
    'SUPERSEDED',
    'ARCHIVED',
];
export const productUsageRoleSchema = z.enum(PRODUCT_USAGE_ROLES);
export const productUsageRolesSchema = z
    .array(productUsageRoleSchema)
    .max(PRODUCT_USAGE_ROLES.length);
export const productMasterProfileStatusSchema = z.enum(PRODUCT_MASTER_PROFILE_STATUSES);
export const supportedLocaleSchema = z.enum(SUPPORTED_LOCALES);
export const structuredJsonSchema = z.lazy(() => z.union([
    z.string().max(20_000),
    z.number().finite(),
    z.boolean(),
    z.null(),
    z.array(structuredJsonSchema).max(500),
    z.record(z.string().max(120), structuredJsonSchema),
]));
export const structuredObjectSchema = z.record(z.string().max(120), structuredJsonSchema);
const unitCode = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_./-]{1,40}$/u);
function isPositiveDecimal(value) {
    try {
        return compareDecimal(value, '0') > 0;
    }
    catch {
        return false;
    }
}
const density = z
    .string()
    .trim()
    .regex(/^\d{1,9}(\.\d{1,9})?$/u)
    .refine(isPositiveDecimal, 'Die Dichte muss größer als null sein.');
const conversionFactor = z
    .string()
    .trim()
    .regex(/^\d{1,12}(\.\d{1,12})?$/u)
    .refine(isPositiveDecimal, 'Der Faktor muss größer als null sein.');
const conversionOffset = z
    .string()
    .trim()
    .regex(/^-?\d{1,12}(\.\d{1,12})?$/u);
export const productMasterTranslationWriteSchema = z.object({
    locale: supportedLocaleSchema,
    internalName: z.string().trim().max(300).nullish(),
    externalName: z.string().trim().max(300).nullish(),
    declarationName: z.string().trim().max(500).nullish(),
    storageInstructions: z.string().trim().max(5_000).nullish(),
});
const productUnitConversionBaseSchema = z.object({
    fromUnitCode: unitCode,
    toUnitCode: unitCode,
    factor: conversionFactor,
    offset: conversionOffset.default('0'),
    roundingScale: z.number().int().min(0).max(12).default(6),
    usesDensity: z.boolean().default(false),
});
export const productUnitConversionWriteSchema = productUnitConversionBaseSchema.refine((value) => value.fromUnitCode !== value.toUnitCode, {
    path: ['toUnitCode'],
    message: 'Quell- und Zieleinheit müssen verschieden sein.',
});
export const productSupplierSnapshotSchema = z.object({
    id: idSchema,
    supplierPartnerId: idSchema,
    supplierName: z.string(),
    supplierSku: z.string().nullable(),
    purchasePrice: moneySchema.nullable(),
    currency: z.string(),
    packagingUnit: z.string().nullable(),
    packagingFactor: z.string().nullable(),
    leadTimeDays: z.number().int(),
    minOrderQuantity: z.string(),
    isPreferredSupplier: z.boolean(),
    notes: z.string().nullable(),
    capturedAt: isoDateTimeSchema,
});
const profileContentSchema = z.object({
    standardUnitCode: unitCode,
    usageRoles: productUsageRolesSchema,
    density: density.nullish(),
    densityUnitCode: unitCode.nullish(),
    originCountryCode: countryCodeSchema.nullish(),
    originRegion: z.string().trim().max(300).nullish(),
    manufacturerName: z.string().trim().max(300).nullish(),
    manufacturerAddress: z.string().trim().max(2_000).nullish(),
    technicalProperties: structuredObjectSchema.default({}),
    qualityProperties: structuredObjectSchema.default({}),
    storageConditions: structuredObjectSchema.default({}),
    shelfLifeDays: z.number().int().min(1).max(36_500).nullish(),
    foodProfile: structuredObjectSchema.nullish(),
    nutritionProfile: structuredObjectSchema.nullish(),
    allergenInformation: structuredObjectSchema.nullish(),
    additiveInformation: structuredObjectSchema.nullish(),
    extensionProfiles: structuredObjectSchema.default({}),
    dmsAttachmentIds: z.array(idSchema).max(100).default([]),
    validFrom: isoDateSchema.nullish(),
    validTo: isoDateSchema.nullish(),
    changeReason: z.string().trim().min(3).max(5_000),
    translations: z
        .array(productMasterTranslationWriteSchema)
        .max(SUPPORTED_LOCALES.length)
        .default([]),
    conversions: z.array(productUnitConversionWriteSchema).max(100).default([]),
});
function validateProfileContent(value, ctx) {
    if (value.density !== null && value.density !== undefined && !value.densityUnitCode) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['densityUnitCode'],
            message: 'Für eine Dichte ist eine Einheit erforderlich.',
        });
    }
    if (value.validFrom && value.validTo && value.validTo < value.validFrom) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['validTo'],
            message: 'Das Gültigkeitsende darf nicht vor dem Beginn liegen.',
        });
    }
    const locales = new Set();
    value.translations.forEach((translation, index) => {
        if (locales.has(translation.locale)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['translations', index, 'locale'],
                message: 'Eine Sprache darf pro Version nur einmal vorkommen.',
            });
        }
        locales.add(translation.locale);
    });
    const conversions = new Set();
    value.conversions.forEach((conversion, index) => {
        const key = `${conversion.fromUnitCode}|${conversion.toUnitCode}`;
        if (conversions.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['conversions', index],
                message: 'Diese Umrechnungsrichtung ist bereits vorhanden.',
            });
        }
        conversions.add(key);
    });
}
export const productMasterProfileCreateSchema = profileContentSchema.superRefine(validateProfileContent);
export const productMasterProfileUpdateSchema = profileContentSchema
    .extend({ optimisticVersion: z.number().int().min(1) })
    .superRefine(validateProfileContent);
export const productMasterProfileTranslationSchema = productMasterTranslationWriteSchema.extend({
    id: idSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const productUnitConversionSchema = productUnitConversionBaseSchema
    .extend({
    id: idSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .refine((value) => value.fromUnitCode !== value.toUnitCode, {
    path: ['toUnitCode'],
    message: 'Quell- und Zieleinheit müssen verschieden sein.',
});
export const productMasterProfileVersionSchema = profileContentSchema
    .omit({
    translations: true,
    conversions: true,
})
    .extend({
    id: idSchema,
    productId: idSchema,
    versionNumber: z.number().int().min(1),
    optimisticVersion: z.number().int().min(1),
    status: productMasterProfileStatusSchema,
    supplierSnapshot: z.array(productSupplierSnapshotSchema),
    translations: z.array(productMasterProfileTranslationSchema),
    conversions: z.array(productUnitConversionSchema),
    submittedAt: isoDateTimeSchema.nullable(),
    submittedById: idSchema.nullable(),
    approvedAt: isoDateTimeSchema.nullable(),
    approvedById: idSchema.nullable(),
    supersededAt: isoDateTimeSchema.nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    createdById: idSchema,
    updatedById: idSchema,
});
export const productMasterProfileListResponseSchema = z.object({
    data: z.array(productMasterProfileVersionSchema),
});
export const productMasterProfileSubmitSchema = z.object({
    optimisticVersion: z.number().int().min(1),
});
export const productMasterProfileApproveSchema = z.object({
    optimisticVersion: z.number().int().min(1),
    decisionNote: z.string().trim().min(3).max(2_000),
});
export const productMasterProfileCopySchema = z.object({
    changeReason: z.string().trim().min(3).max(5_000),
});
export const productMasterProfileArchiveSchema = z.object({
    optimisticVersion: z.number().int().min(1),
    reason: z.string().trim().min(3).max(2_000),
});
export const productUsageRoleUpdateSchema = z.object({
    usageRoles: productUsageRolesSchema,
    expectedUpdatedAt: isoDateTimeSchema,
});
export const productUsageRoleStateSchema = z.object({
    productId: idSchema,
    usageRoles: productUsageRolesSchema,
    updatedAt: isoDateTimeSchema,
});
export const rawMaterialListQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    role: productUsageRoleSchema.optional(),
    includeArchived: z.preprocess((value) => {
        if (value === 'true')
            return true;
        if (value === 'false')
            return false;
        return value;
    }, z.boolean().default(false)),
});
export const rawMaterialListItemSchema = z.object({
    id: idSchema,
    articleNumber: z.string(),
    name: z.string(),
    status: z.string(),
    baseUnit: z.string(),
    usageRoles: productUsageRolesSchema,
    latestProfileVersion: z.number().int().nullable(),
    latestProfileStatus: productMasterProfileStatusSchema.nullable(),
    updatedAt: isoDateTimeSchema,
});
export const rawMaterialListResponseSchema = z.object({ data: z.array(rawMaterialListItemSchema) });
export const productProfileDefinitionWriteSchema = z.object({
    key: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z][A-Z0-9_]{1,49}$/u),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2_000).nullish(),
    domainKey: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z][a-z0-9_-]{1,49}$/u),
    fieldSchema: structuredObjectSchema,
});
export const productProfileDefinitionSchema = productProfileDefinitionWriteSchema.extend({
    id: idSchema,
    schemaVersion: z.number().int().min(1),
    isActive: z.boolean(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    archivedAt: isoDateTimeSchema.nullable(),
});
export const productProfileDefinitionListResponseSchema = z.object({
    data: z.array(productProfileDefinitionSchema),
});
//# sourceMappingURL=plm-material.js.map